/**
 * TPRA Home Recent Events API Request Hook
 * Fetches list of recent events (breaches, SOC2 expirations) for the dashboard
 */

import { get } from "@/infra/rest";
import { APIEndpoints } from "@/shared/constants/Endpoints";
import { tpraHomeQueryKeys } from "@/shared/constants/QueryKeys";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { RecentEventsResponse } from "../types";
import { RecentEventsResponseSchema } from "../validation/schemas";

/**
 * Fetches recent events data from the API
 * @returns Validated recent events response
 * @throws Error if API request fails or response validation fails
 */
async function fetchRecentEvents(): Promise<RecentEventsResponse> {
  const response = await get<RecentEventsResponse>({
    url: `${APIEndpoints.TPRAHomeRecentEvents}?days=30`,
  });

  // Validate response with Zod schema
  const validated = RecentEventsResponseSchema.parse(response);

  return validated;
}

/**
 * React Query hook for fetching TPRA home recent events
 *
 * Provides:
 * - Automatic caching with TanStack Query
 * - Loading and error states
 * - Automatic refetching on mount
 * - Retry logic for failed requests
 *
 * @returns React Query result with recent events data
 *
 * @example
 * ```typescript
 * function RecentEventsSection() {
 *   const { data, isLoading, isError, refetch } = useGetRecentEvents();
 *
 *   if (isLoading) return <EventsSkeleton />;
 *   if (isError) return <SectionError onRetry={refetch} />;
 *
 *   return <EventsList events={data.events} />;
 * }
 * ```
 */
export function useGetRecentEvents(): UseQueryResult<RecentEventsResponse, Error> {
  return useQuery({
    queryKey: tpraHomeQueryKeys.events(),
    queryFn: fetchRecentEvents,

    // Caching Strategy
    staleTime: 5 * 60 * 1000, // 5 minutes - events don't change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes cache retention

    // Retry Strategy (exponential backoff)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),

    // Refetch Behavior
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
}
